import type { UseQueryResult, DefaultError } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '../clients';
import { queryKeys } from '../lib/query-keys';
import { getHighschoolsPath } from '../utils/api-paths';
import { isDirectApiMode } from '../utils/api-paths';
import {
  getCpContextHighschoolId,
  isEmbeddedInSucceed,
  isQaSucceedApiProxyDev,
} from '../utils/cp-context';
import { useAuth } from '../context/auth-context';
import { SucceedUserLevel } from '../types/succeed-user-session';
import { isSchoolIndividualCoursePlannerProductLevel } from '../utils/succeed-course-planner-product-level-cache';
import type { School } from '../types';

/**
 * Fetches the list of schools (highschools) the current user has access to.
 * Mirrors the legacy API: GET /highschools (served by succeed-api, not course-planner-service).
 *
 * In local/direct mode the backend doesn't expose /highschools, so we return mock data
 * (55+ schools so clusters/pathways publish & active school drawers can exercise pagination).
 * In QA/prod (embedded in Succeed) the call goes through succeed-api which proxies to the real endpoint.
 */

const SEED_MOCK_SCHOOLS: School[] = [
  { nid: 'school-1', name: 'Lincoln High School' },
  { nid: 'school-2', name: 'Washington High School' },
  { nid: '88881USPU', name: 'Jefferson Academy' },
  { nid: 'school-4', name: 'Roosevelt High School' },
  { nid: 'school-5', name: 'Kennedy High School' },
];

const GENERATED_MOCK_SCHOOL_COUNT = 50;

const MOCK_SCHOOLS: School[] = [
  ...SEED_MOCK_SCHOOLS,
  ...Array.from({ length: GENERATED_MOCK_SCHOOL_COUNT }, (_, index) => {
    const n = index + SEED_MOCK_SCHOOLS.length + 1;
    return {
      nid: `mock-hs-${n}`,
      name: `District Mock High School ${String(n).padStart(2, '0')}`,
    };
  }),
];

/**
 * Returns school options scoped to the current Course Planner subscription.
 *
 * @param enabled - Whether the school query may run.
 * @returns School-only sessions receive their embedded school; other sessions retain district options.
 */
export function useGetSchools(enabled = true): UseQueryResult<School[], DefaultError> {
  const apiClient = useApiClient();
  const path = getHighschoolsPath();
  const { succeedSubscriptionScope } = useAuth();
  const needsResolvedSession = isEmbeddedInSucceed() || isQaSucceedApiProxyDev();
  const isScopeResolved = !needsResolvedSession || succeedSubscriptionScope.status === 'resolved';
  const isSchoolOnly = needsResolvedSession
    ? succeedSubscriptionScope.productLevel === SucceedUserLevel.SCHOOL_INDIVIDUAL
    : isSchoolIndividualCoursePlannerProductLevel();
  const schoolNid = getCpContextHighschoolId() ?? succeedSubscriptionScope.highschoolId;

  return useQuery({
    queryKey: !isScopeResolved
      ? [...queryKeys.schools.list(), { scopeStatus: succeedSubscriptionScope.status }]
      : isSchoolOnly
        ? [...queryKeys.schools.list(), { schoolNid: schoolNid ?? null }]
        : queryKeys.schools.list(),
    queryFn: async (): Promise<School[]> => {
      if (isDirectApiMode()) {
        return MOCK_SCHOOLS;
      }

      const response = await apiClient.get<School[]>(path);
      return Array.isArray(response.data) ? response.data : [];
    },
    select: (schools) =>
      !isScopeResolved
        ? []
        : isSchoolOnly
          ? schools.filter((school) => school.nid === schoolNid)
          : schools,
    enabled: enabled && isScopeResolved,
    staleTime: 5 * 60 * 1000,
    retry: false,
    throwOnError: false,
  });
}
